'use client';

import React, { JSX, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { AppDispatch, RootState } from '@/store/store';
import { ICON_SEARCH } from '@/constants/icons';
import { PL_PERMALINK_UNIONARENA } from '@/constants/product';
import { TX_LOADING, TX_SEARCH } from '@/constants/strings';
import ProductLine from '@/models/product-lines/ProductLine';
import { simulateTab } from '@/utils/general';
import { productSetsByProductLine } from '@/actions/product';
import { productLineSetAdHocData } from '@/actions/product-line';
import Checkbox from '@/components/Input/Checkbox';
import Icon from '@/components/Icons/Icon';
import LoadingIcon from '@/components/Icons/LoadingIcon';

// Prop types
interface FilterElementUnionarenaSetProps {
	config: {
		key: string;
		label: string;
	};
	filterValue: string[];
	setFilter: (filter: Record<string, string[]>) => void;
	setCustomFilterOptions: (options: Record<string, unknown>) => void;
}

export const FilterElementUnionarenaSet = (props: FilterElementUnionarenaSetProps): JSX.Element => {

	// Dispatch
	const dispatch = useDispatch<AppDispatch>();

	// Props
	const { config, filterValue, setFilter, setCustomFilterOptions } = props;

	// Local state
	const [inputSearch, setInputSearch] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [expanded, setExpanded] = useState<boolean>(true);

	// Accessing Redux state
	const productLineData = useSelector((state: RootState) => state.productLine?.data);

	// Internationalization
	const { t } = useTranslation();

	// Methods
	const getSetData = () => {
		if (productLineData && productLineData[PL_PERMALINK_UNIONARENA] && productLineData[PL_PERMALINK_UNIONARENA].sets) {
			return productLineData[PL_PERMALINK_UNIONARENA].sets;
		}
		return null;
	};

	const getCompositeId = (opt: string | number) => {
		const paramKey = config.key;
		const valueStr = opt.toString();
		return `${paramKey}|${valueStr}`;
	};

	const getOptions = () => {
		const setData = getSetData();
		if (!inputSearch) {
			return setData || [];
		}

		const filteredOptions: any[] = [];
		const needle = inputSearch.toLowerCase();

		for (const elem of setData) {
			const displayString = elem.name.toLowerCase();
			if (displayString.includes(needle)) {
				filteredOptions.push(elem);
			}
		}
		return filteredOptions;
	};

	const changeSearch = (evt: React.ChangeEvent<HTMLInputElement>) => {
		setInputSearch(evt.target.value);
	};

	const handleKeyPress = (evt: React.KeyboardEvent<HTMLInputElement>) => {
		if (evt.key === 'Enter') {
			evt.preventDefault();
			simulateTab();
		}
	};

	const clearInput = () => {
		setInputSearch('');
	};

	const toggleExpanded = () => {
		setExpanded(!expanded);
	};

	const toggleCheckbox = (evt: React.ChangeEvent<HTMLInputElement>) => {
		if (evt) { evt.stopPropagation(); }

		const updatedFilters = [...filterValue];
		const idx = updatedFilters.indexOf(evt.target.value);

		if (idx > -1) {
			updatedFilters.splice(idx, 1);
		} else {
			updatedFilters.push(evt.target.value);
		}

		setFilter({
			[config.key]: updatedFilters,
		});
	};

	// Effects
	useEffect(() => {
		const loadData = async () => {
			const existingData = getSetData();
			if (!existingData) {
				setLoading(true);

				const fixedLine = new ProductLine({ permalink: PL_PERMALINK_UNIONARENA });

				const setData = await dispatch(productSetsByProductLine(fixedLine))
					.catch((errResp: unknown) => {
						console.error(errResp);
					});

				setLoading(false);

				if (!setData) { return; }

				setCustomFilterOptions({
					[config.key]: setData.data,
				});

				dispatch(productLineSetAdHocData({
					[PL_PERMALINK_UNIONARENA]: {
						sets: setData.data,
					}
				}));
			} else {
				setCustomFilterOptions({
					[config.key]: existingData,
				});
			}
		};
		loadData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Render
	return (
		<div className={'FilterElement FilterElementCheckboxes FilterElementUnionarenaSet'}>
			<div className='feFilterTitle'>
				<div className={'feFilterTitleValue EllipsisElement'}>{t(config.label)}</div>
				<div className='feFilterTitleToggle' onClick={toggleExpanded}>
					<div className='feFilterTitleToggleElement FlexCenter'>
						{expanded ?
							<>&mdash;</> :
							<>+</>
						}
					</div>
				</div>
			</div>
			<div className={`feFilterBody ${expanded ? 'open' : ''}`}>
				{loading ?
					<div className='feFilterLoading'>
						<div className='feLoadingWrapper'>
							<div className='feLoadingIcon'>
								<LoadingIcon />
							</div>
							<div className='feLoadingLabel'>{t(TX_LOADING)}</div>
						</div>
					</div> :

					<div className='feFilterBodyLiner'>
						<div className='multiQuickSearchInput'>
							<input
								type='text'
								name={t(TX_SEARCH)}
								value={inputSearch}
								placeholder={t(TX_SEARCH)}
								onChange={changeSearch}
								onKeyPress={handleKeyPress} />
							<div className='searchOverlay'>
								<Icon value={ICON_SEARCH} iconClass='searchIcon' />
							</div>
							{inputSearch ?
								<div className='clearOverlay' onClick={clearInput}>&times;</div> :
								null
							}
						</div>
						<div className='feFilterBodyLinerScroll'>
							{getOptions().map((opt: any, i: number) => {
								return <div className='feCheckboxWrapper' key={i}>
									<div className='feCheckboxInputWrapper'>
										<Checkbox
											name={config.key}
											id={getCompositeId(opt.code)}
											value={opt.code}
											checked={filterValue.includes(opt.code.toString())}
											onChange={toggleCheckbox} />
									</div>
									<label
										htmlFor={getCompositeId(opt.code)}
										className='feCheckboxValueWrapper'>
										{opt.name}
									</label>
								</div>;
							})}
						</div>
					</div>
				}
			</div>
		</div>
	);
};

export default FilterElementUnionarenaSet;
